import * as React from 'react';
import { useMutation, useQuery } from 'convex/react';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Grid,
  IconButton,
  Select,
  Stack,
  Text,
  Tooltip,
  useToast,
} from '@fragments-sdk/ui';
import {
  ArrowClockwise,
  ArrowSquareOut,
  CheckCircle,
  Eye,
  EyeSlash,
  Key,
  Plugs,
} from '@phosphor-icons/react';
import { api } from '@/convex';
import { HighlightedCodeBlock } from '@/components/code/HighlightedCodeBlock';
import {
  ContentState,
  DashboardLayout,
  WorkspacePage,
  WorkspacePageHeader,
} from '@/components/layout';
import { maskApiKey, useOrgApiKey } from '@/hooks/useOrgApiKey';
import { useOrgContext } from '@/hooks/useOrgContext';
import { buildSettingsPath } from '@/lib/routes';
import {
  FEATURE_DETAILS,
  PLANS,
  cheapestPlanWithFeature,
  hasFeature,
  type PlanId,
} from '../../../../convex/billing/plans';
import s from './mcp-developer-setup.module.scss';
import {
  buildClaudeCommand,
  buildCursorDeeplink,
  buildMcpConfigJson,
  ToolLogo,
} from './mcp-developer-setup-helpers';
import {
  ClientCard,
  CopyButton,
  CopyField,
  DiscoveryDisclosure,
  SectionCard,
} from './mcp-developer-setup-ui';

const DEFAULT_MCP_ORIGIN = 'https://app.usefragments.com';

function resolveMcpOrigin() {
  return typeof window === 'undefined'
    ? DEFAULT_MCP_ORIGIN
    : window.location.origin;
}

export function McpDeveloperSetup() {
  return (
    <DashboardLayout activeNav="Developer Setup" flush>
      <McpDeveloperSetupInner />
    </DashboardLayout>
  );
}

function McpDeveloperSetupInner() {
  const { success: toastSuccess, error: toastError } = useToast();
  const { orgId, org, activeRole, isLoading: orgLoading } = useOrgContext();
  const plan = (org?.plan ?? 'free') as PlanId;
  const hasMachineApi = hasFeature(plan, 'apiAccess');
  const setup = useQuery(
    api.orgs.getDeveloperSetup,
    orgId && hasMachineApi ? { orgId } : 'skip',
  );
  const setCanonicalBinding = useMutation(
    api.orgs.setCanonicalDesignSystemBinding,
  );
  const canManage = activeRole === 'owner' || activeRole === 'admin';
  const { apiKey: revealedKey, rotate: rotateApiKey } = useOrgApiKey({
    orgId,
    enabled: canManage && hasMachineApi,
  });

  const [selectedBindingId, setSelectedBindingId] = React.useState('');
  const [keyRevealed, setKeyRevealed] = React.useState(false);
  const [mcpOrigin, setMcpOrigin] = React.useState(DEFAULT_MCP_ORIGIN);

  React.useEffect(() => {
    setMcpOrigin(resolveMcpOrigin());
  }, []);

  React.useEffect(() => {
    setSelectedBindingId('');
    setKeyRevealed(false);
  }, [orgId]);

  React.useEffect(() => {
    if (!setup?.selectedBindingId) return;
    setSelectedBindingId(String(setup.selectedBindingId));
  }, [setup?.selectedBindingId]);

  async function handleRotateKey() {
    try {
      await rotateApiKey();
      setKeyRevealed(false);
      toastSuccess(
        'API key rotated',
        'Update local environment variables and MCP config.',
      );
    } catch (error) {
      toastError(
        'Unable to rotate API key',
        error instanceof Error ? error.message : undefined,
      );
    }
  }

  async function handleSetCanonicalBinding() {
    if (!orgId || !selectedBindingId) return;
    try {
      const result = await setCanonicalBinding({
        orgId,
        bindingId: selectedBindingId as any,
      });
      toastSuccess('Canonical source set', result.repoFullName);
    } catch (error) {
      toastError(
        'Unable to set the canonical source',
        error instanceof Error ? error.message : undefined,
      );
    }
  }

  if (orgLoading || (!!orgId && hasMachineApi && setup === undefined)) {
    return (
      <ContentState
        className={s.loadingState}
        icon={<Plugs size={18} />}
        title="Loading developer setup…"
        minHeight={240}
      />
    );
  }

  if (!hasMachineApi) {
    const requiredPlan = cheapestPlanWithFeature('apiAccess') ?? 'pro';
    return (
      <ContentState
        className={s.loadingState}
        icon={<Plugs size={18} />}
        title={`${FEATURE_DETAILS.apiAccess.label} requires ${PLANS[requiredPlan].label}`}
        description={`Your workspace is on ${PLANS[plan].label}. Upgrade to ${PLANS[requiredPlan].label} to use live MCP queries and machine API keys.`}
        minHeight={280}
      />
    );
  }

  if (!setup) {
    return (
      <ContentState
        className={s.loadingState}
        icon={<Plugs size={18} />}
        title="Developer setup unavailable"
        description="Developer setup is unavailable for this organization."
        minHeight={240}
      />
    );
  }

  // Read the origin live at call time so copy/install actions are correct even
  // before the post-mount effect settles (avoids a stale prod-origin copy).
  const mcpEndpoint = `${mcpOrigin}/api/mcp`;
  const liveEndpoint = () => `${resolveMcpOrigin()}/api/mcp`;
  const protectedResourceUrl = `${mcpOrigin}/.well-known/oauth-protected-resource/api/mcp`;
  const authorizationServerUrl = `${mcpOrigin}/.well-known/oauth-authorization-server`;

  const maskedKey = revealedKey ? maskApiKey(revealedKey) : null;
  const displayKey = revealedKey
    ? keyRevealed
      ? revealedKey
      : (maskedKey ?? revealedKey)
    : 'YOUR_API_KEY';
  const displayConfigJson = buildMcpConfigJson(displayKey, mcpEndpoint);

  const hasEligibleBindings = setup.eligibleBindings.length > 0;
  const settingsPath = setup.org.slug
    ? buildSettingsPath(setup.org.slug)
    : null;
  const needsSourceChoice = setup.status === 'canonical_binding_required';
  const resolvedBinding =
    setup.eligibleBindings.find((binding) => binding.isCanonical) ?? null;
  const showSourcePicker =
    canManage &&
    hasEligibleBindings &&
    (needsSourceChoice || setup.eligibleBindings.length > 1);
  const sourceUnchanged =
    !selectedBindingId || selectedBindingId === setup.selectedBindingId;

  return (
    <WorkspacePage className={s.contentPanel}>
      <Stack className={s.contentStack} gap="xl">
        <WorkspacePageHeader
          title="Developer Setup"
          description="Connect AI clients and CI to your hosted Fragments MCP server."
        />

        {/* 1 · Source — what MCP and the catalog API actually serve */}
        <SectionCard
          title="Design system source"
          description="MCP and the catalog API serve components and tokens from one canonical repo."
        >
          {!hasEligibleBindings ? (
            <EmptyState className={s.sourceEmpty}>
              <EmptyState.Icon>
                <Plugs size={20} />
              </EmptyState.Icon>
              <EmptyState.Title>
                No design system connected yet
              </EmptyState.Title>
              <EmptyState.Description>
                Connect a design-system repo and ingest its contracts to power
                MCP and the catalog API. Hosted MCP works now — it just returns
                an empty catalog until a source is set.
              </EmptyState.Description>
              {settingsPath ? (
                <EmptyState.Actions>
                  <Button variant="primary" size="sm" asChild>
                    <Link to={settingsPath as any}>Connect a repo</Link>
                  </Button>
                </EmptyState.Actions>
              ) : null}
            </EmptyState>
          ) : (
            <Stack gap="md">
              {needsSourceChoice ? (
                <Alert severity="warning" className={s.notice}>
                  <Alert.Icon />
                  <Alert.Content>
                    {canManage
                      ? 'Choose which connected repo is the canonical source for this workspace.'
                      : 'Ask an owner or admin to choose the canonical source for this workspace.'}
                  </Alert.Content>
                </Alert>
              ) : null}

              {resolvedBinding ? (
                <Stack
                  direction="row"
                  gap="sm"
                  align="center"
                  wrap
                  className={s.sourceSummary}
                >
                  <Badge variant="outline" size="sm">
                    Canonical
                  </Badge>
                  <Text as="span" size="sm" weight="medium" font="mono">
                    {resolvedBinding.repoFullName}
                  </Text>
                  <Text as="span" size="xs" color="tertiary">
                    {setup.componentCount} components · {setup.tokenCount}{' '}
                    tokens
                  </Text>
                </Stack>
              ) : null}

              {showSourcePicker ? (
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  gap="sm"
                  align="stretch"
                  className={s.sourceControls}
                >
                  <Select
                    value={selectedBindingId || null}
                    onValueChange={(value) => setSelectedBindingId(value ?? '')}
                    placeholder="Select a canonical source"
                    className={s.bindingSelect}
                  >
                    <Select.Trigger aria-label="Canonical source" />
                    <Select.Content>
                      {setup.eligibleBindings.map((binding) => (
                        <Select.Item
                          key={String(binding.bindingId)}
                          value={String(binding.bindingId)}
                        >
                          {binding.repoFullName} · {binding.componentCount}{' '}
                          components
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSetCanonicalBinding}
                    disabled={sourceUnchanged}
                  >
                    <CheckCircle size={14} />
                    {needsSourceChoice ? 'Set source' : 'Update source'}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          )}
        </SectionCard>

        {/* 2 · Connect an interactive editor over OAuth */}
        <SectionCard
          title="Connect your editor"
          description="Add the Fragments MCP server to your AI client. Interactive clients authenticate with OAuth — no API key required."
        >
          <Stack gap="lg">
            <Grid columns={{ base: 1, sm: 2 }} gap="md" alignItems="stretch">
              <ClientCard
                icon={<ToolLogo target="claude" />}
                title="Claude Code"
                description="Run this in your terminal, then approve Fragments at the OAuth prompt."
                command={buildClaudeCommand(mcpEndpoint)}
              >
                <CopyButton
                  getValue={() => buildClaudeCommand(liveEndpoint())}
                  label="Copy command"
                  fullWidth
                />
              </ClientCard>
              <ClientCard
                icon={<ToolLogo target="cursor" />}
                title="Cursor"
                description="One click installs the server, then approve Fragments OAuth in Cursor."
              >
                <Button variant="primary" size="sm" fullWidth asChild>
                  <a href={buildCursorDeeplink(mcpEndpoint)}>
                    <ArrowSquareOut size={14} weight="bold" />
                    Add to Cursor
                  </a>
                </Button>
              </ClientCard>
            </Grid>

            <Stack gap="sm">
              <CopyField
                label="Endpoint"
                value={mcpEndpoint}
                helper="Paste into any other MCP client — OAuth metadata is discovered automatically."
              />
              <DiscoveryDisclosure>
                <CopyField
                  label="Protected resource"
                  value={protectedResourceUrl}
                />
                <CopyField
                  label="Authorization server"
                  value={authorizationServerUrl}
                />
                <CopyField
                  label="Scopes"
                  value="mcp:read mcp:write"
                  copyable={false}
                />
              </DiscoveryDisclosure>
            </Stack>
          </Stack>
        </SectionCard>

        {/* 3 · CI / headless access over a workspace API key */}
        <SectionCard
          title="CI & automation"
          description="Headless tools — CI jobs and scripts — authenticate with a workspace API key. Prefer OAuth for local editors so writes like autofix PRs carry your identity."
          actions={
            <>
              {revealedKey ? (
                <Tooltip
                  content={keyRevealed ? 'Hide API key' : 'Reveal API key'}
                >
                  <IconButton
                    variant="subtle"
                    size="md"
                    aria-label={keyRevealed ? 'Hide API key' : 'Reveal API key'}
                    onClick={() => setKeyRevealed((value) => !value)}
                  >
                    {keyRevealed ? <EyeSlash size={15} /> : <Eye size={15} />}
                  </IconButton>
                </Tooltip>
              ) : null}
              <CopyButton
                getValue={() =>
                  buildMcpConfigJson(
                    revealedKey ?? 'YOUR_API_KEY',
                    liveEndpoint(),
                  )
                }
                label="Copy config"
                disabled={!revealedKey}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRotateKey}
                disabled={!canManage}
              >
                <ArrowClockwise size={14} />
                Rotate key
              </Button>
            </>
          }
        >
          <Stack gap="lg">
            {!canManage ? (
              <Alert severity="info" className={s.notice}>
                <Alert.Icon />
                <Alert.Content>
                  Ask an owner or admin to reveal or rotate the workspace API
                  key. The OAuth connection above works for everyone.
                </Alert.Content>
              </Alert>
            ) : null}
            <HighlightedCodeBlock
              code={displayConfigJson}
              language="json"
              className={s.codeBlock}
              lazy={false}
            />
            <Stack direction="row" gap="sm" align="start">
              <Key size={14} />
              <Text as="p" size="xs" color="secondary">
                Active key {setup.apiKeyPrefix}… · API-key requests read
                catalogs and run safe tools. User-scoped writes require OAuth.
              </Text>
            </Stack>
          </Stack>
        </SectionCard>
      </Stack>
    </WorkspacePage>
  );
}
